/* Namie — back-in-stock bell.
 *
 * Shows the bell only while the SELECTED variant is unbuyable, and hides it the
 * moment the shopper picks one that is in stock.
 *
 * Written against THIS theme's variant picker (Symmetry-family — see
 * assets/variant-picker.js), not Dawn's. The three things that matter:
 *
 *   * `<variant-picker>` dispatches `on:variant:change` (bubbling, with
 *     detail.variant) on every change.
 *   * Its updateVariantInput() sets the hidden `[name="id"]` value AND fires a
 *     bubbling `change` event on that input. That is the most authoritative
 *     signal there is — it is the value the cart would actually receive — and
 *     it is why this does not bother with a MutationObserver: setting .value
 *     from JS never triggers an attribute mutation, so watching attributes
 *     would silently never fire.
 *   * Option names live on `.option-selector[data-option]`, and the selected
 *     value is either `input:checked` (listed) or
 *     `.custom-select__option[aria-selected="true"]` (dropdown).
 *
 * Both signals funnel into one idempotent sync(), so a duplicate firing is
 * harmless and a missed one is picked up by the next.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-namie-bell]');
  if (!root) return;

  var panel = root.querySelector('[data-namie-bell-panel]');
  var openBtn = root.querySelector('[data-namie-bell-open]');
  var form = root.querySelector('[data-namie-bell-form]');
  var emailInput = root.querySelector('#namie-bell-email');
  var variantLine = root.querySelector('[data-namie-bell-variant]');
  var msg = root.querySelector('[data-namie-bell-msg]');
  var submit = root.querySelector('[data-namie-bell-submit]');
  var ENDPOINT = root.getAttribute('data-endpoint') || 'https://staff.namiehome.co.uk/api/storefront/stock-alert';
  var STORE_KEY = 'namie.stockAlert.email';

  var variants = [];
  try {
    variants = JSON.parse(root.querySelector('[data-namie-bell-variants]').textContent) || [];
  } catch (e) {
    return; // No variant data means no honest way to decide — show nothing.
  }
  var byId = {};
  variants.forEach(function (v) { byId[String(v.id)] = v; });

  var current = null;
  var submitted = {}; // variant id -> true, so a double click cannot double post

  /**
   * Move the bell from the end of <body> (where theme.liquid renders it) to
   * where it belongs — the last thing inside <buy-buttons>, exactly where a
   * {% render %} in main-product.liquid would have put it. See the snippet's
   * header comment for why it is rendered there and moved rather than rendered
   * in place. It starts hidden, so nothing is ever visible in the wrong spot.
   */
  function place() {
    if (root.getAttribute('data-placed')) return true;
    var f = productForm();
    var target = document.querySelector('buy-buttons') || (f && f.parentNode);
    if (!target) return false;
    target.appendChild(root);
    root.setAttribute('data-placed', '1');
    return true;
  }

  // The product form this bell belongs to. `.js-product-form` is this theme's
  // class; the action check is the portable fallback.
  function productForm() {
    return root.closest('.js-product') && root.closest('.js-product').querySelector('.js-product-form')
      || document.querySelector('.js-product-form')
      || document.querySelector('form[action*="/cart/add"]');
  }

  function selectedId() {
    var f = productForm();
    var input = f && f.querySelector('[name="id"]');
    if (input && input.value && byId[String(input.value)]) return String(input.value);
    var q = new URLSearchParams(window.location.search).get('variant');
    if (q && byId[q]) return q;
    // Fall back to the first variant, matching what Shopify shows by default.
    return variants.length ? String(variants[0].id) : null;
  }

  // "Cap Colour: Black · Reed Colour: White" — the words the shopper actually
  // chose from, rather than the slash-joined "Black / White", which tells them
  // nothing. A single-variant product has no picker at all, so this correctly
  // returns nothing and the caller suppresses the line.
  function variantLabel(v) {
    var parts = [];
    document.querySelectorAll('.option-selector').forEach(function (sel) {
      var name = sel.getAttribute('data-option');
      var value = null;
      if (sel.getAttribute('data-selector-type') === 'dropdown') {
        var opt = sel.querySelector('.custom-select__option[aria-selected="true"]');
        value = opt && opt.getAttribute('data-value');
      } else {
        var checked = sel.querySelector('input:checked');
        value = checked && checked.value;
      }
      if (name && value) parts.push(name + ': ' + value);
    });
    if (parts.length) return parts.join(' · ');
    return v && !/^default title$/i.test(v.title || '') ? v.title : '';
  }

  function show(el, on) { if (el) el.hidden = !on; }

  function say(text, bad) {
    if (!msg) return;
    msg.textContent = text || '';
    msg.className = 'namie-bell__msg' + (bad ? ' is-bad' : ' is-ok');
    show(msg, Boolean(text));
  }

  function sync() {
    // If the buy buttons are not in the DOM yet, stay hidden and wait — the
    // events below will call back.
    if (!place()) { show(root, false); return; }
    var id = selectedId();
    if (!id) return;
    var v = byId[id];
    if (!v) {
      // Mid-selection on a picker where no complete variant is chosen yet.
      // Say nothing rather than guessing.
      show(root, false);
      return;
    }
    var changed = id !== current;
    current = id;

    // The one rule: the bell exists only for a variant that cannot be bought.
    show(root, !v.available);
    if (v.available) { show(panel, false); return; }

    if (changed) {
      // A new variant is a new alert, so the panel resets rather than carrying
      // the previous variant's "you're on the list" over to this one.
      show(panel, false);
      say('');
      if (submit) { submit.disabled = false; submit.textContent = 'Set alert'; }
    }
    if (variantLine) variantLine.textContent = variantLabel(v);
    if (submitted[id]) say('You are on the list for this one.', false);
  }

  if (openBtn) {
    openBtn.addEventListener('click', function () {
      var opening = panel.hidden;
      show(panel, opening);
      if (!opening) return;
      // Pre-fill: the signed-in customer's address, else whatever they used
      // last time on this browser.
      if (emailInput && !emailInput.value) {
        var known = root.getAttribute('data-known-email');
        if (!known) { try { known = localStorage.getItem(STORE_KEY) || ''; } catch (e) { known = ''; } }
        emailInput.value = known || '';
      }
      if (emailInput) emailInput.focus();
    });
  }

  if (form) {
    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var v = byId[current];
      if (!v) return;
      var email = (emailInput.value || '').trim();
      if (!email || email.indexOf('@') < 0 || email.indexOf('.') < 0) {
        say('Please check that email address.', true);
        emailInput.focus();
        return;
      }
      submit.disabled = true;
      submit.textContent = 'Setting…';
      say('');

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          variantId: v.id,
          email: email,
          company: (form.querySelector('[name="company"]') || {}).value || '',
          marketingOptIn: Boolean(form.querySelector('[name="marketingOptIn"]:checked')),
          productId: root.getAttribute('data-product-id'),
          productTitle: root.getAttribute('data-product-title'),
          handle: root.getAttribute('data-handle'),
          variantTitle: v.title,
          sku: v.sku,
        }),
      })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok || res.body.ok === false) {
            say(res.body.message || 'Sorry — we could not set that alert. Please try again.', true);
            submit.disabled = false;
            submit.textContent = 'Set alert';
            return;
          }
          submitted[current] = true;
          try { localStorage.setItem(STORE_KEY, email); } catch (e) { /* private mode */ }
          say(
            res.body.already
              ? 'You were already on the list — we will email you when it is back.'
              : 'Done. We will email you as soon as it is back.',
            false
          );
          submit.textContent = 'On the list';
        })
        .catch(function () {
          say('Sorry — we could not reach us just then. Please try again.', true);
          submit.disabled = false;
          submit.textContent = 'Set alert';
        });
    });
  }

  // --- keeping up with the variant picker -----------------------------------

  // The picker's own event, and the change it fires on the hidden id input.
  // Both are delegated on document so a section re-render cannot leave the
  // listener bound to an element that no longer exists.
  document.addEventListener('on:variant:change', sync);
  document.addEventListener('change', function (ev) {
    if (ev.target && ev.target.name === 'id') sync();
  });
  window.addEventListener('popstate', sync);
  // The script is deferred so the DOM is parsed, but <buy-buttons> is a custom
  // element and a section re-render can replace it. One more pass on load
  // covers both without a polling loop.
  window.addEventListener('load', sync);

  sync();
})();
